import { createContext, useCallback, useContext, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { setStoredProgrammeTypeValue } from "../../../shared/utils/platformAuth";
import {
  SourceType,
  type NavbarContextValue,
  type NavbarProviderProps,
  type Source
} from "./types";

const NavbarContext = createContext<NavbarContextValue | null>(null);

export function NavbarProvider({
  theme,
  user,
  executeAction,
  children
}: NavbarProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const navigateFromSource = useCallback(
    (source?: Source) => {
      if (!source) return;

      if (source.programType) {
        setStoredProgrammeTypeValue(source.programType);
      }

      switch (source.type) {
        case SourceType.Route: {
          const [pathname, existingSearch] = source.value.split("?");
          if (source.programType) {
            const params = new URLSearchParams(existingSearch);
            params.set("programType", source.programType);
            navigate({ pathname, search: params.toString() });
          } else {
            navigate(source.value);
          }
          break;
        }
        case SourceType.Url:
          window.open(source.value, "_blank", "noopener,noreferrer");
          break;
        case SourceType.Action:
          executeAction?.(source.key);
          break;
        case SourceType.None:
          // No route of its own (e.g. My Queue's program-type children) —
          // still keep the current URL's programType param in sync, since
          // the queue-info/list APIs read it from there, not localStorage.
          if (source.programType) {
            const params = new URLSearchParams(location.search);
            params.set("programType", source.programType);
            navigate(
              { pathname: location.pathname, search: params.toString() },
              { replace: true }
            );
          }
          break;
        default:
          break;
      }
    },
    [executeAction, navigate, location.pathname, location.search]
  );

  const isRouteActive = useCallback(
    (source?: Source) => {
      if (!source || source.type !== SourceType.Route) return false;
      return location.pathname === source.value;
    },
    [location.pathname]
  );

  const value = useMemo(
    () => ({
      theme,
      user,
      navigateFromSource,
      isRouteActive
    }),
    [theme, user, navigateFromSource, isRouteActive]
  );

  return (
    <NavbarContext.Provider value={value}>{children}</NavbarContext.Provider>
  );
}

export function useNavbarContext(): NavbarContextValue {
  const context = useContext(NavbarContext);
  if (!context) {
    throw new Error("useNavbarContext must be used within NavbarProvider");
  }
  return context;
}
